import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ChevronDown, Mail, Phone, Save } from "lucide-react";

export const STATUSES = ["new", "contacted", "won", "lost"];

const STATUS_STYLE = {
  new: "bg-blue-50 text-blue-800 border-blue-200",
  contacted: "bg-amber-50 text-amber-800 border-amber-200",
  won: "bg-emerald-50 text-emerald-800 border-emerald-200",
  lost: "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const sourceLabel = (s) => (s === "promo-video" ? "Promo video" : s?.startsWith("amblemead.biz") ? "Website" : s);

function StatusPill({ status }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLE[status]}`}>
      {status}
    </span>
  );
}

function Details({ req, onUpdated }) {
  const [notes, setNotes] = useState(req.notes);
  const [saving, setSaving] = useState(false);
  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/marketing/demo-requests/${req.id}`, patch);
      onUpdated(data);
      if (patch.notes !== undefined) setNotes(data.notes);
      toast.success(patch.status ? `Marked as ${patch.status}` : "Notes saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="px-4 pb-4 grid gap-4 md:grid-cols-2" data-testid={`demo-request-details-${req.id}`}>
      <div className="space-y-2 text-sm text-zinc-700">
        <a href={`mailto:${req.email}`} className="inline-flex items-center gap-1.5 hover:underline"><Mail className="h-4 w-4" />{req.email}</a>
        {req.phone && <a href={`tel:${req.phone}`} className="flex items-center gap-1.5 hover:underline"><Phone className="h-4 w-4" />{req.phone}</a>}
        {req.postcode && <p className="text-zinc-500">Post code: {req.postcode}</p>}
        <p className="whitespace-pre-wrap bg-zinc-50 border border-zinc-100 rounded-sm p-3 text-zinc-800">{req.message || "No message."}</p>
      </div>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button key={s} type="button" disabled={saving || req.status === s} onClick={() => save({ status: s })}
              data-testid={`demo-request-status-${s}-${req.id}`}
              className={`text-xs font-semibold capitalize px-3 py-1.5 rounded-sm border transition-colors duration-150 disabled:cursor-default ${
                req.status === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:border-zinc-950"
              }`}>{s}</button>
          ))}
        </div>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={4000}
          placeholder="Private notes, e.g. called Tuesday, follow up next week" data-testid={`demo-request-notes-${req.id}`} />
        <Button type="button" onClick={() => save({ notes })} disabled={saving || notes === req.notes}
          className="h-9 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white" data-testid={`demo-request-save-notes-${req.id}`}>
          <Save className="h-4 w-4 mr-1.5" /> Save notes
        </Button>
      </div>
    </div>
  );
}

export default function DemoRequestRow({ req, onUpdated }) {
  const [open, setOpen] = useState(false);
  return (
    <li data-testid={`demo-request-row-${req.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`demo-request-toggle-${req.id}`}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-50 transition-colors duration-150">
        <StatusPill status={req.status} />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-zinc-950 truncate">{req.company}</span>
          <span className="block text-xs text-zinc-500 truncate">{req.name} · {sourceLabel(req.source)}</span>
        </span>
        <span className="text-xs text-zinc-500 whitespace-nowrap hidden sm:inline">{new Date(req.created_at).toLocaleDateString("en-GB")}</span>
        <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <Details req={req} onUpdated={onUpdated} />}
    </li>
  );
}
